import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { userErrorMessage, type EbookMove, type EbookQuestion } from '@wx-weiqi/contracts';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  DoubleArrowLeftIcon,
  DoubleArrowRightIcon,
  ReloadIcon,
} from '@radix-ui/react-icons';
import { EbookClient } from '../services/ebooks';
import { BoardSurface } from './BoardSurface';
import { EbookEditorBoardFrame } from './EbookEditorBoardFrame';
import { ClassroomZoomControl } from './ClassroomZoomControl';
import { boardViewportForCorner, type HomeworkBoardCorner } from '../domain/homework-board-focus';
import { ebookMoveLabels } from '../services/ebook-variations';
import { ebookTrialCompleted, ebookTrialUndoPly } from '../services/ebook-trial-navigation';

type Preview = Awaited<ReturnType<EbookClient['practicePreview']>>;

/** Teacher-only preview: never creates an attempt or writes student learning records. */
export function EbookEditorTrial({
  client,
  documentId,
  question: q,
  onBack,
  onBusyChange,
}: {
  client: EbookClient;
  documentId: string;
  question: EbookQuestion;
  onBack: () => void;
  onBusyChange?: (busy: boolean) => void;
}) {
  const [result, setResult] = useState<Preview | null>(null);
  const [viewed, setViewed] = useState<{ ply: number; board: Preview['board'] } | null>(null);
  const [corner, setCorner] = useState<HomeworkBoardCorner | null>(null);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState('');
  const [manualSubmitted, setManualSubmitted] = useState(false);
  useLayoutEffect(() => {
    onBusyChange?.(busy);
    return () => onBusyChange?.(false);
  }, [busy, onBusyChange]);
  const inFlight = useRef(false);
  const mounted = useRef(true);
  const run = async (fn: () => Promise<void>) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError('');
    try {
      await fn();
    } catch (failure) {
      if (mounted.current) setError(userErrorMessage(failure, '试做加载失败，请重试'));
    } finally {
      inFlight.current = false;
      if (mounted.current) setBusy(false);
    }
  };
  const restart = () =>
    run(async () => {
      const next = await client.practicePreview(documentId, q, []);
      if (!mounted.current) return;
      setResult(next);
      setViewed(null);
      setManualSubmitted(false);
    });
  useEffect(() => {
    mounted.current = true;
    void restart();
    return () => {
      mounted.current = false;
    };
  }, []);
  const completed = ebookTrialCompleted(q.mode, result?.status, manualSubmitted);
  const board = viewed?.board ??
    result?.board ?? { stones: q.stones, currentPlayer: q.currentPlayer };
  const ply = viewed?.ply ?? result?.moves.length ?? 0;
  const move = (point: EbookMove['point']) => {
    if (!result || busy || completed || viewed) return;
    void run(async () => {
      const next = await client.practicePreview(documentId, q, result.moves, {
        color: result.board.currentPlayer,
        point,
      });
      if (mounted.current) setResult(next);
    });
  };
  const navigate = (index: number) => {
    if (!result || busy || index < 0 || index > result.moves.length) return;
    if (index === result.moves.length) {
      setViewed(null);
      return;
    }
    void run(async () => {
      const position = await client.trial(documentId, q, result.moves.slice(0, index));
      if (mounted.current) setViewed({ ply: index, board: position });
    });
  };
  const undo = () => {
    if (!result || busy || !result.moves.length) return;
    const previous = ebookTrialUndoPly(result.moves, q.mode, q.currentPlayer);
    void run(async () => {
      const next = await client.practicePreview(documentId, q, result.moves.slice(0, previous));
      if (!mounted.current) return;
      setResult(next);
      setViewed(null);
      setManualSubmitted(false);
    });
  };
  const status = !result
    ? '正在准备试做…'
    : manualSubmitted
      ? '已提交试做，正式作答由教师批改'
      : result.status === 'CORRECT'
        ? '回答正确'
        : result.status === 'INCORRECT'
          ? '回答错误'
          : result.status === 'PENDING_REVIEW'
            ? '请摆出完整变化后提交试做'
            : result.status === 'UNKNOWN'
              ? '此变化尚未收录'
              : '请在棋盘上落子';
  return (
    <div className="ebook-editor-workspace ebook-editor-trial">
      <div className="ebook-editor-trial-heading">
        <strong>第 {q.number} 题</strong>
        <span>{q.goal}</span>
        <span className="ebook-badge">试做预览</span>
      </div>
      {error && (
        <p role="alert" className="ebook-editor-error">
          {error}
        </p>
      )}
      <div className="ebook-editor-panes">
        <section className="ebook-editor-pane ebook-editor-board-pane">
          <div className="ebook-editor-board-tools">
            <strong>
              第 {ply} 手 · {board.currentPlayer === 'BLACK' ? '黑棋' : '白棋'}
            </strong>
            <button aria-label="试做初始局面" disabled={busy || !ply} onClick={() => navigate(0)}>
              <DoubleArrowLeftIcon />
            </button>
            <button
              aria-label="试做上一手"
              disabled={busy || !ply}
              onClick={() => navigate(ply - 1)}
            >
              <ChevronLeftIcon />
            </button>
            <button
              aria-label="试做下一手"
              disabled={busy || ply >= (result?.moves.length ?? 0)}
              onClick={() => navigate(ply + 1)}
            >
              <ChevronRightIcon />
            </button>
            <button
              aria-label="试做末手"
              disabled={busy || ply >= (result?.moves.length ?? 0)}
              onClick={() => navigate(result?.moves.length ?? 0)}
            >
              <DoubleArrowRightIcon />
            </button>
            <ClassroomZoomControl
              zoomed={corner !== null}
              onSelect={setCorner}
              onRestore={() => setCorner(null)}
            />
          </div>
          <EbookEditorBoardFrame>
            <BoardSurface
              size={q.size}
              stones={board.stones}
              boardAppearance="wood"
              stoneAppearance="shell"
              viewport={corner === null ? null : boardViewportForCorner(corner)}
              lastMove={null}
              stoneLabels={ebookMoveLabels(
                q.size,
                board.stones,
                (result?.moves ?? []).slice(0, ply),
              )}
              initialAnnotations={q.marks.map((m) =>
                m.kind === 'LABEL'
                  ? { point: m.point, kind: 'LABEL' as const, text: m.label }
                  : { point: m.point, kind: m.kind },
              )}
              keyboardNavigation
              isActionable={() => !busy && Boolean(result) && !completed && !viewed}
              onPoint={move}
            />
          </EbookEditorBoardFrame>
        </section>
        <section className="ebook-editor-pane ebook-editor-trial-info">
          <h3>以学生视角检查这道题</h3>
          <span className="ebook-badge">{q.mode === 'AUTO' ? '自动批改' : '教师批改'}</span>
          <div className="ebook-editor-trial-message" role="status">
            <strong>{status}</strong>
            <p>本次试做不会计入学生学习记录。</p>
            {viewed && (
              <button disabled={busy} onClick={() => setViewed(null)}>
                返回当前作答
              </button>
            )}
          </div>
          <label className="ebook-editor-check">
            <input type="checkbox" />
            题目要求是否清晰
          </label>
          <label className="ebook-editor-check">
            <input type="checkbox" />
            正确与错误变化是否符合预期
          </label>
          {q.hint && (
            <details>
              <summary>查看题目提示</summary>
              <p>{q.hint}</p>
            </details>
          )}
          <p className="ebook-muted">完成后可返回修改答案。</p>
          <div className="ebook-editor-trial-actions">
            <button disabled={busy} onClick={() => void restart()}>
              <ReloadIcon />
              重新试做
            </button>
            <button disabled={busy || !result?.moves.length} onClick={undo}>
              悔棋
            </button>
            <button
              disabled={busy || !result || completed || Boolean(viewed)}
              onClick={() => move(null)}
            >
              虚着
            </button>
            {q.mode === 'MANUAL' && (
              <button
                disabled={busy || !result?.moves.length || manualSubmitted}
                onClick={() => setManualSubmitted(true)}
              >
                提交试做
              </button>
            )}
          </div>
        </section>
      </div>
      <footer className="ebook-editor-footer">
        <span>教师试做 · 不影响学生记录</span>
        <button className="primary" disabled={busy} onClick={onBack}>
          返回答案编辑 <ChevronRightIcon />
        </button>
      </footer>
    </div>
  );
}
